<template>
    <div>
        <b-datatable
            v-if="can(`api.v1.reports.${endpoint}`)"
            ref="dataTable"
            :key="datatableKey"
            :columns="columns"
            :endpoint="$root.route(`api.v1.reports.${endpoint}`)"
            :hidden-columns="hiddenColumns"
            has-filter-selector
            item-key="id"
            :grid-title="gridTitle"
            :keep-filters="false"
        >
            <template #filters="{ filters }">
                <v-col cols="4">
                    <v-text-field
                        v-model="filters.filters.search"
                        :label="t('reports::label.name')"
                        class="flex-grow-0"
                        clearable
                        dense
                        rounded
                        outlined
                        tabindex="1"
                    ></v-text-field>
                </v-col>
            </template>

            <template #[`item.color_result`]="{ item }">
                <div :class="`blood-status ${item.color_result}--text`">
                    <div :class="`status ${item.color_result}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.color_result"></span>
                </div>
            </template>
            <template #[`item.max_dispensing`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.max_dispensing ? t('patients::max_dispensing.yes') : t('patients::max_dispensing.no')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.date_taken`]="{ item }">
                {{ item.date_taken ? moment(item.date_taken).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.patient.name`]="{ item }">
                <div v-if="canAccessRoute" class="link" @click="$root.goTo(itemUrl(item))">
                    {{ item.patient.name }}
                </div>
                <div v-else>
                    {{ item.patient.name }}
                </div>
            </template>

            <template #[`item.validity_date`]="{ item }">
                {{ item.validity_date ? moment(item.validity_date).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.created_at`]="{ item }">
                {{ item.created_at ? moment(item.created_at).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.health_status`]="{ item }">
                <div v-if="healthStatus(item)" class="grid-status">
                    <span>
                        {{ healthStatus(item) }}
                    </span>
                </div>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip
                        v-if="can('api.v1.patients.blood-results.update') && item.editable"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(itemUrl(item, true))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('users::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="
                            (can('api.v1.patients.blood-results.show') && !item.editable) ||
                            (!can('api.v1.patients.blood-results.update') && item.editable)
                        "
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(itemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.details') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
    </div>
</template>
<script>
export default {
    name: 'ReportDataTableComponent',
    props: {
        endpoint: {
            type: String,
            required: true,
            default: '',
        },
    },
    data() {
        return {
            datatableKey: 0,
            hiddenColumns: ['id', 'patient.id'],
            astmHiddenColumns: [
                'id',
                'analyzer_name',
                'pharmacy_site_id',
                'ip',
                'dms_id',
                'is_registered',
                'entered_by',
            ],
            columns: [
                {
                    text: 'ID',
                    value: 'id',
                    sortable: false,
                    width: 170,
                    fields: 'id,assigned_by.id,created_by.id',
                },
                {
                    text: t('reports::grid.label.color_result'),
                    value: 'color_result',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('reports::grid.label.patient'),
                    value: 'patient.name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('reports::grid.label.dmsId'),
                    value: 'patient.dms_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('reports::grid.label.nhsId'),
                    value: 'patient.nhs_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('reports::grid.label.dateOfValidity'),
                    value: 'validity_date',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('reports::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'patient.id,editable',
                    align: 'end',
                    width: 100,
                },
            ],
            astmColumns: [
                {
                    text: t('reports::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('reports::grid.label.astm_name'),
                    value: 'name',
                    sortable: false,
                    width: 200,
                },
                {
                    text: t('reports::grid.label.analyzer_name'),
                    value: 'analyzer_name',
                    sortable: false,
                    width: 180,
                },
                {
                    text: t('reports::grid.label.pharmacy_site_id'),
                    value: 'pharmacy_site_id',
                    sortable: false,
                    width: 180,
                },
                {
                    text: t('reports::grid.label.ip'),
                    value: 'ip',
                    sortable: false,
                    width: 160,
                },
                {
                    text: t('reports::grid.label.dmsId'),
                    value: 'dms_id',
                    sortable: false,
                    width: 140,
                },
                {
                    text: t('reports::grid.label.is_registered'),
                    value: 'is_registered',
                    sortable: false,
                    width: 140,
                },
                {
                    text: t('reports::grid.label.created_at'),
                    value: 'created_at',
                    width: 140,
                    sortable: false,
                },
            ],
            filtersOptions: {},
        };
    },
    computed: {
        selectedPeriod() {
            return this.$store.state.reports.selectedPeriod;
        },
        canAccessRoute() {
            return this.can('patients.preview');
        },
        gridTitle() {
            return this.$store.state.reports.gridTitle;
        },
        selectedPeriodMeta() {
            return this.$store.state.reports.reportChartData.meta.filters;
        },
    },
    watch: {
        selectedPeriod(val) {
            if (this.$refs.dataTable) {
                this.$refs.dataTable.selectedPeriod = val;
                this.$refs.dataTable.applyFilters();
            }
        },
        selectedPeriodMeta(meta) {
            if (meta.start_date && meta.end_date) {
                return this.setSelectedPeriodString();
            }
        },
    },
    created() {},
    destroyed() {},
    mounted() {
        if (this.$refs.dataTable) {
            this.$refs.dataTable.selectedPeriod = this.selectedPeriod;
            this.selectedPeriodMeta;
        }
    },
    methods: {
        setSelectedPeriodString() {
            this.$store.dispatch(
                'reports/setSelectedPeriodString',
                `${moment(this.selectedPeriodMeta.start_date).format('DD.MM.YYYY')} - ${moment(
                    this.selectedPeriodMeta.end_date
                ).format('DD.MM.YYYY')}`
            );
        },
        itemUrl(item, edit = false) {
            return route(edit ? 'patients.edit' : 'patients.preview', {
                patient: item.patient.id,
                load_lood_result: true,
                blood_result_id: item.id,
            });
        },
        healthStatus(item) {
            if (item && item.health_status) {
                return _.map(item.health_status, (status) => {
                    return t('patients::health_status.' + status);
                }).join(', ');
            }
            return false;
        },
    },
};
</script>
